import { useMemo } from "react";

import { AnimatePresence, motion } from "framer-motion";
import moment from "moment";

import cardLoading from "@assets/sales-v3-platform-loading-cell.gif";
import { useUser } from "@components/user-context";
import { formatting } from "@north-beam/nb-common";
import { type MetricPrecision } from "@north-beam/nb-common/formatting";
import { useClientSettings } from "@pages/mmm/hooks/use-client-settings";

import { PacingStatus } from "../../shared/pacing-status";
import { usePerformanceTrackingSpendData } from "../hooks/use-performance-tracking-spend-data";

import { Slider } from "./slider";

type Props = {
  data?: {
    actual: number;
    budgeted: number;
    target: number;
  };
};

export function useBudgetedSpendToDate() {
  const { user } = useUser();
  const { trackingSpendData, loading } = usePerformanceTrackingSpendData();

  const adoptedBudgetedSpendToDate = useMemo(() => {
    const today = moment.tz(user.timezone).endOf("d");

    let _budgeted = 0;

    trackingSpendData.forEach((spendColumn) => {
      spendColumn.dailyMetrics.forEach((i) => {
        if (moment(i.date).isSameOrBefore(today)) {
          _budgeted += i.budgeted ?? 0;
        }
      });
    });

    return _budgeted;
  }, [trackingSpendData, user.timezone]);

  return { adoptedBudgetedSpendToDate, loading };
}

export const SpendTile = ({ data }: Props) => {
  const { user } = useUser();
  const { adoptedBudgetedSpendToDate, loading } = useBudgetedSpendToDate();

  const {
    percentOfCycleCompleted,
    formattedMixToDateRange,
    formattedCycleDateRange,
  } = useClientSettings();

  if (!data || loading) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="border border-v2-secondary rounded w-full h-[270px] flex-center"
        >
          <img className="h-1/2" src={cardLoading} alt="Loading" />
        </motion.div>
      </AnimatePresence>
    );
  }

  const formatter = (
    value: number,
    precision: MetricPrecision = "approximate",
  ) =>
    formatting.formatCurrency(value, {
      precision,
      code: user.currencies.currentCurrency,
    });

  const isCompleted = percentOfCycleCompleted === 1;

  return (
    <motion.div className="border border-v2-secondary rounded p-4 w-full min-w-0">
      <div className="flex justify-between mb-3">
        <div className="font-semibold text-xl truncate flex-1 mr-2 flex-grow">
          Spend
        </div>
        <PacingStatus
          className="rounded px-3 h-7 font-semibold text-xs"
          prefix={isCompleted ? "Completed" : "Pacing"}
          actual={data.actual}
          budgeted={adoptedBudgetedSpendToDate}
        />
      </div>
      <div className="text-xs tracking-wide relative flex items-center justify-between">
        <div className="bg-cosmic opacity-10 rounded-t absolute h-7 w-full"></div>
        <div className="py-1.5 pl-2 flex items-center space-x-2 font-semibold">
          <div className="tracking-wide">
            MIX {isCompleted ? "COMPLETED" : "TO DATE"}
          </div>
          <div className="text-cosmic text-xs">
            {`${formatting.formatPercentage(percentOfCycleCompleted, {
              precision: "approximate",
            })} complete`}
          </div>
        </div>
        <div className="py-1.5 pr-2 text-gray+2">{formattedMixToDateRange}</div>
      </div>
      <div className=" h-36 bg-v2-secondary flex items-center">
        <Slider
          actualColor="#C825A8"
          actual={data.actual}
          actualText="SPEND"
          budgeted={adoptedBudgetedSpendToDate}
          target={data.target}
          formatter={formatter}
          dateRangeText={formattedMixToDateRange}
          targetIconTooltipContent={
            <div className="font-semibold">
              <div className="text-xs tracking-wide mb-1">
                TOTAL PERIOD BUDGET
              </div>
              <div className="text-gray+2 font-normal">
                {formattedCycleDateRange}
              </div>
              <div className="text-lg text-cosmic">
                {formatter(data.target, "exact")}
              </div>
            </div>
          }
        />
      </div>
      <div className="flex justify-between items-center mt-2">
        <div className="flex flex-col text-xs">
          <div className="font-semibold tracking-wide">TOTAL PERIOD BUDGET</div>
          <div className="text-gray+2">{formattedCycleDateRange}</div>
        </div>
        <div className="flex items-center space-x-2">
          {/* <i className="fa-regular fa-bullseye-arrow h-3 w-3 bg-v2-primary border border-v2-secondary rounded-full p-1 text-interstellar-1" /> */}
          <div className="text-lg font-semibold">{formatter(data.target)}</div>
        </div>
      </div>
    </motion.div>
  );
};
